import { useState } from "react";
import { Plus, Search, IndianRupee, Wallet, Clock, MoreVertical, Eye, Download } from "lucide-react";
import { AddPaymentDialog } from "./AddPaymentDialog";
import { pdf } from "@react-pdf/renderer";
import ReceiptPDF from "../Finance/ReceiptPDF";

// Utility for classes
const cn = (...classes) => classes.filter(Boolean).join(" ");

const Card = ({ className = "", children }) => (
  <div className={`rounded-lg border bg-white text-card-foreground shadow-sm ${className}`}>
    {children}
  </div>
);

const CardContent = ({ className = "", children }) => (
  <div className={`p-6 ${className}`}>{children}</div>
);

const Button = ({ variant = "default", size = "default", className = "", children, ...props }) => {
  const baseStyles = "inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50";
  const variants = {
    default: "bg-sm-primary text-white shadow hover:bg-sm-primary/90",
    destructive: "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90",
    outline: "border border-input bg-transparent shadow-sm hover:bg-sm-accent hover:text-sm-accent-foreground",
    secondary: "bg-sm-secondary text-sm-secondary-foreground shadow-sm hover:bg-sm-secondary/80",
    ghost: "hover:bg-sm-accent hover:text-sm-accent-foreground",
    link: "text-sm-primary underline-offset-4 hover:underline",
  };
  const sizes = {
    default: "h-9 px-4 py-2",
    sm: "h-8 rounded-md px-3 text-xs",
    lg: "h-10 rounded-md px-8",
    icon: "h-9 w-9",
  };
  return (
    <button className={cn(baseStyles, variants[variant], sizes[size], className)} {...props}>
      {children}
    </button>
  );
};

const Input = ({ className = "", ...props }) => (
  <input className={`flex h-9 w-full rounded-md border border-input bg-transparent pr-3 pl-9 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 ${className}`} {...props} />
);

const Badge = ({ variant = "default", className = "", children }) => {
  const baseStyles = "inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-normal transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2";
  const variants = {
    default: "border-transparent bg-sm-primary text-sm-primary-foreground hover:bg-sm-primary/80",
    secondary: "border-transparent bg-sm-secondary/10 text-sm-secondary hover:bg-sm-secondary/20",
    outline: "text-foreground",
  };
  // Override variant logic for custom classes if passed directly
  const finalClass = className.includes("bg-") ? `${baseStyles} ${className}` : cn(baseStyles, variants[variant], className);
  return (
    <div className={finalClass}>
      {children}
    </div>
  );
};

export function ContractorPaymentsTab({
  payments,
  workOrders,
  contractor,
  onAddPayment,
}) {
  const [searchQuery, setSearchQuery] = useState("");
  const [isAddPaymentOpen, setIsAddPaymentOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState(null);

  const handleViewReceipt = async (payment) => {
    const transactionData = {
      ...payment,
      transaction_id: payment.payment_id || payment.id,
      contractor_name: contractor.name,
      contractor: contractor,
      work_order_no: payment.work_order_no || payment.workOrderId,
      payment_mode: payment.payment_mode || payment.mode,
      reference_no: payment.reference_no || payment.reference,
      site_name: payment.site_name || payment.projectName,
      attachment_file: payment.attachment_file
    };
    
    const doc = <ReceiptPDF transactionData={transactionData} type="contractor" />;
    const blob = await pdf(doc).toBlob();
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank');
    setActiveMenu(null);
  };

  const handleDownloadReceipt = async (payment) => {
    const transactionData = {
      ...payment,
      transaction_id: payment.payment_id || payment.id,
      contractor_name: contractor.name,
      contractor: contractor,
      work_order_no: payment.work_order_no || payment.workOrderId,
      payment_mode: payment.payment_mode || payment.mode,
      reference_no: payment.reference_no || payment.reference,
      site_name: payment.site_name || payment.projectName,
      attachment_file: payment.attachment_file
    };

    const doc = <ReceiptPDF transactionData={transactionData} type="contractor" />;
    const blob = await pdf(doc).toBlob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Receipt_${transactionData.transaction_id}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setActiveMenu(null);
  };

  const filteredPayments = payments.filter(p => {
    if (searchQuery &&
      !p.workOrderId.toLowerCase().includes(searchQuery.toLowerCase()) &&
      !p.projectName.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false;
    }
    return true;
  });

  const formatCurrency = (amount) => {
    if (amount >= 100000) {
      return `₹${(amount / 100000).toFixed(2)}L`;
    }
    return `₹${amount.toLocaleString()}`;
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const getStatusBadgeClass = (status) => {
    switch (status.toLowerCase()) {
      case "success":
        return "bg-green-500/10 text-green-600 border-green-500/30";
      case "pending":
        return "bg-sm-accent/10 text-sm-accent border-sm-accent/30";
      case "failed":
        return "bg-red-500/10 text-red-600 border-red-500/30";
      default:
        return "bg-muted text-muted-foreground";
    }
  };

  const totalPayments = payments.reduce((sum, p) => sum + p.amount, 0);
  const totalDue = contractor.totalDue;
  const retentionHeld = Math.round(contractor.totalWorkOrderValue * 0.05); // 5% retention

  const handleAddPayment = (contractor_id) => {
    onAddPayment(contractor_id);
    setIsAddPaymentOpen(false);
  };

  return (
    <div className="space-y-4">
      {/* Header Analytics */}
      {/* <div className="grid grid-cols-3 gap-4">
        <Card className="border-border/60 shadow-none">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-green-500/10 flex items-center justify-center">
              <IndianRupee className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-xl font-semibold text-foreground">{formatCurrency(totalPayments)}</p>
              <p className="text-xs text-muted-foreground">Total Payments Made</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-border/60 shadow-none">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-red-500/10 flex items-center justify-center">
              <Clock className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <p className="text-xl font-semibold text-foreground">{formatCurrency(totalDue)}</p>
              <p className="text-xs text-muted-foreground">Total Due</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-border/60 shadow-none">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-sm-accent/10 flex items-center justify-center">
              <Wallet className="w-5 h-5 text-sm-accent" />
            </div>
            <div>
              <p className="text-xl font-semibold text-foreground">{formatCurrency(retentionHeld)}</p>
              <p className="text-xs text-muted-foreground">Retention Held</p>
            </div>
          </CardContent>
        </Card>
      </div> */}

      {/* Filters Row */}
      <div className="flex items-center justify-between gap-4">
        <span className="text-sm text-muted-foreground">
          {filteredPayments.length} payments
        </span>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search payments..."
              className="h-9 text-sm pl-9 w-56 border-border/60"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <Button
            className="bg-sm-primary hover:bg-sm-primary/90 text-sm-primary-foreground h-9 text-sm gap-2"
            onClick={() => setIsAddPaymentOpen(true)}
          >
            <Plus className="h-4 w-4" />
            Add Payment
          </Button>
        </div>
      </div>

      {/* Payments Table */}
      <Card className="border-border/60 shadow-none">
        <CardContent className="p-0">
          <table className="w-full caption-bottom text-sm">
            <thead className="[&_tr]:border-b">
              <tr className="border-b transition-colors hover:bg-transparent border-border/60">
                <th className="h-10 px-3 text-left align-middle font-medium text-muted-foreground text-xs">Date</th>
                <th className="h-10 px-2 text-left align-middle font-medium text-muted-foreground text-xs">Work Order</th>
                <th className="h-10 px-2 text-left align-middle font-medium text-muted-foreground text-xs">Project</th>
                <th className="h-10 px-2 text-left align-middle font-medium text-muted-foreground text-xs">Type</th>
                <th className="h-10 px-2 text-left align-middle font-medium text-muted-foreground text-xs">Mode</th>
                <th className="h-10 px-2 align-middle font-medium text-muted-foreground text-xs text-right">Amount</th>
                <th className="h-10 px-2 text-left align-middle font-medium text-muted-foreground text-xs">Reference</th>
                <th className="h-10 px-2 align-middle font-medium text-muted-foreground text-xs text-center">Status</th>
                <th className="h-10 px-2 text-left align-middle font-medium text-muted-foreground text-xs">Remarks</th>
                <th className="h-10 px-2 text-right align-middle font-medium text-muted-foreground text-xs"></th>
              </tr>
            </thead>
            <tbody className="[&_tr:last-child]:border-0">
              {filteredPayments.map((payment) => (
                <tr
                  key={payment.id}
                  className="hover:bg-muted/30 border-b border-border/40 transition-colors"
                >
                  <td className="p-2 align-middle py-3 text-sm">{formatDate(payment.date)}</td>
                  <td className="p-2 align-middle text-sm font-medium text-sm-primary">#{payment.work_order_no || payment.workOrderId}</td>
                  <td className="p-2 align-middle text-sm">{payment.site_name || payment.projectName}</td>
                  <td className="p-2 align-middle">
                    <Badge variant="outline" className="text-xs font-normal border-border">
                      {payment.type}
                    </Badge>
                  </td>
                  <td className="p-2 align-middle">
                    <Badge variant="secondary" className="text-xs font-normal bg-sm-secondary/10 text-sm-secondary border-0">
                      {payment.payment_mode || payment.mode}
                    </Badge>
                  </td>
                  <td className="p-2 align-middle text-right">
                    <span className="text-sm font-semibold text-green-600">{formatCurrency(payment.amount)}</span>
                  </td>
                  <td className="p-2 align-middle text-sm text-muted-foreground">{payment.reference_no || payment.reference}</td>
                  <td className="p-2 align-middle text-center">
                    <Badge className={`text-xs font-normal ${getStatusBadgeClass(payment.status || "success")}`}>
                      {payment.status || "Success"}
                    </Badge>
                  </td>
                  <td className="p-2 align-middle text-sm text-muted-foreground max-w-[120px] truncate">
                    {payment.remarks || "-"}
                  </td>
                  <td className="p-2 align-middle text-right relative">
                    <button 
                      className="p-1 hover:bg-muted rounded-md transition-colors"
                      onClick={(e) => {
                        e.stopPropagation();
                        setActiveMenu(activeMenu === payment.id ? null : payment.id);
                      }}
                    >
                      <MoreVertical className="h-4 w-4 text-muted-foreground" />
                    </button>
                    
                    {activeMenu === payment.id && (
                      <div className="absolute right-0 mt-1 w-32 bg-white border border-border/60 rounded-md shadow-lg z-50 py-1">
                        <button 
                          className="w-full px-3 py-1.5 text-left text-xs hover:bg-muted flex items-center gap-2"
                          onClick={() => handleViewReceipt(payment)}
                        >
                          <Eye className="h-3 w-3" /> View
                        </button>
                        <button 
                          className="w-full px-3 py-1.5 text-left text-xs hover:bg-muted flex items-center gap-2"
                          onClick={() => handleDownloadReceipt(payment)}
                        >
                          <Download className="h-3 w-3" /> Download
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}

              {filteredPayments.length === 0 && (
                <tr className="border-b border-border/40">
                  <td colSpan={10} className="p-4 align-middle py-12 text-center">
                    <div className="flex flex-col items-center">
                      <IndianRupee className="h-10 w-10 text-muted-foreground/50 mb-2" />
                      <p className="text-sm text-muted-foreground">No payments found</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Add Payment Dialog */}
      <AddPaymentDialog
        open={isAddPaymentOpen}
        onOpenChange={setIsAddPaymentOpen}
        workOrders={workOrders}
        onAddPayment={handleAddPayment}
      />
    </div>
  );
}
